/**
 * Google Publisher Tag (GPT) Loader Utility
 * Ensures GPT script is loaded and ready before executing ad code
 */

import { isLighthouseClient } from '@/lib/isLighthouse';

// Track GPT script loading state
let gptScriptLoaded = false;
let gptScriptLoading = false;
let gptLoadCallbacks: Array<() => void> = [];

// Navigation counter for unique ad IDs
let navigationCounter = 0;
let lastPathname = '';

// When infinite scroll is active, skip destroying all slots on URL changes
// (infinite scroll uses history.replaceState for cosmetic URL updates, not real navigation)
let infiniteScrollActive = false;

/**
 * Mark infinite scroll mode as active/inactive.
 * When active, incrementNavigationCounter will skip destroyAllSlots()
 * to prevent killing inline ad slots on cosmetic URL updates.
 */
export function setInfiniteScrollActive(active: boolean): void {
  infiniteScrollActive = active;
}

/**
 * Initialize GPT and load script if needed
 * Calls callback when GPT is ready
 */
export function initGPT(callback?: () => void): void {
  // Initialize googletag command queue (Google's recommended pattern)
  (window as any).googletag = (window as any).googletag || { cmd: [] };

  // Never load the real GPT library for Lighthouse/PSI/synthetic-monitor
  // probes. Every caller of this loader (PageAdRenderer, ArticleListInfinite,
  // ArticleMultiAds, StickyAds, InArticleAdRenderer, VisualStory,
  // SmartLayoutAds) renders as part of page *content*, not layout chrome, so
  // none of them were covered by layout.tsx's server-side `!isLighthouse`
  // gate on SmartLayoutAdsWrapper — they all reached here and loaded the
  // full GPT library (~3s+ of script evaluation), dominating TBT/LCP on
  // every lab run. Gating centrally here (instead of at each call site)
  // means the googletag.cmd stub still exists so callers don't throw, but
  // nothing ever gets pushed to the real network/script — no visible ad,
  // no CLS impact (callers already reserve their own layout space).
  if (isLighthouseClient()) {
    gptScriptLoaded = false;
    return;
  }

  // If GPT is already ready, call callback immediately
  if ((window as any).googletag?.apiReady || gptScriptLoaded) {
    gptScriptLoaded = true;
    callback?.();
    return;
  }

  // Add callback to queue if provided
  if (callback) {
    gptLoadCallbacks.push(callback);
  }

  // Check if GPT script is already in the document or loading
  if (gptScriptLoading) {
    return;
  }

  const existingScript = document.querySelector('script[src*="securepubads.g.doubleclick.net/tag/js/gpt.js"]');

  if (!existingScript) {
    gptScriptLoading = true;

    // Load GPT script
    const script = document.createElement('script');
    script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    script.async = true;

    script.onload = () => {
      gptScriptLoaded = true;
      gptScriptLoading = false;

      // Execute all queued callbacks
      const callbacks = [...gptLoadCallbacks];
      gptLoadCallbacks = [];
      callbacks.forEach(cb => cb());
    };

    script.onerror = () => {
      gptScriptLoading = false;
      // Still try to execute callbacks in case GPT loads another way
      const callbacks = [...gptLoadCallbacks];
      gptLoadCallbacks = [];
      callbacks.forEach(cb => cb());
    };

    document.head.appendChild(script);
  } else {
    // Script exists but may not be loaded yet
    // Use cmd queue to detect when it's ready
    (window as any).googletag.cmd.push(() => {
      gptScriptLoaded = true;
      const callbacks = [...gptLoadCallbacks];
      gptLoadCallbacks = [];
      callbacks.forEach(cb => cb());
    });
  }
}

/**
 * Execute ad scripts - waits for GPT to be ready first
 */
export function executeAdScripts(scripts: { src?: string; content?: string }[]): void {
  // CMS-authored ad code is raw, arbitrary HTML/JS — some snippets bootstrap
  // GPT themselves via an inline (non-`src`) <script> block rather than a
  // plain `<script src="...gpt.js">` tag, so the `initGPT()` gate below
  // doesn't stop them: they'd still run via `new Function()` further down
  // and load gpt.js on their own. Bail out completely instead of relying on
  // initGPT() alone.
  if (isLighthouseClient()) return;

  // Ensure GPT script is loading (but don't wait for it)
  initGPT();

  // Ensure googletag.cmd exists
  (window as any).googletag = (window as any).googletag || { cmd: [] };
  const googletag = (window as any).googletag;

  for (const scriptInfo of scripts) {
    try {
      if (scriptInfo.src) {
        if (scriptInfo.src.includes('gpt.js')) continue;
        const newScript = document.createElement('script');
        newScript.src = scriptInfo.src;
        newScript.async = true;
        document.head.appendChild(newScript);
      } else if (scriptInfo.content) {
        const scriptContent = scriptInfo.content;
        // Push googletag commands directly to cmd queue — GPT processes them when ready
        if (scriptContent.includes('googletag')) {
          googletag.cmd.push(() => {
            try { new Function(scriptContent)(); } catch {}
          });
        } else {
          try { new Function(scriptContent)(); } catch {}
        }
      }
    } catch {}
  }
}

/**
 * Parse ad code to separate HTML and scripts
 */
export function parseAdCode(code: string): { html: string; scripts: { src?: string; content?: string }[] } {
  if (!code) return { html: '', scripts: [] };

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = code;

  const scripts: { src?: string; content?: string }[] = [];
  const scriptElements = tempDiv.querySelectorAll('script');

  scriptElements.forEach((script) => {
    // Skip non-JavaScript script types (e.g., application/json, application/ld+json)
    const scriptType = (script.type || '').toLowerCase();
    if (scriptType && scriptType !== 'text/javascript' && scriptType !== 'application/javascript') {
      script.remove();
      return;
    }

    if (script.src) {
      scripts.push({ src: script.src });
    } else if (script.textContent) {
      scripts.push({ content: script.textContent });
    }
    script.remove();
  });

  return { html: tempDiv.innerHTML, scripts };
}

/**
 * Clean ad code - remove surrounding quotes and unescape
 */
export function cleanAdCode(adCode: string): string {
  let cleanedCode = adCode.trim();

  if (cleanedCode.startsWith('"') && cleanedCode.endsWith('"')) {
    cleanedCode = cleanedCode.slice(1, -1);
  }

  // Handle Unicode escape sequences
  cleanedCode = cleanedCode
    .replace(/\\u003C/gi, "<")
    .replace(/\\u003E/gi, ">")
    .replace(/\\u0026/gi, "&")
    .replace(/\\u0027/gi, "'")
    .replace(/\\u0022/gi, '"')
    .replace(/\\""/g, '"')
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/""/g, '"');

  // Also try JSON.parse if it looks like a JSON string
  if (cleanedCode.startsWith('"') || cleanedCode.includes('\\u')) {
    try {
      cleanedCode = JSON.parse(`"${cleanedCode.replace(/^"|"$/g, '')}"`);
    } catch {
      // If JSON.parse fails, use the manually cleaned code
    }
  }

  return cleanedCode;
}

/**
 * Refresh all ad slots on the page
 * Call this on client-side navigation to reload ads
 */
export function refreshAllAds(): void {
  initGPT(() => {
    try {
      const googletag = (window as any).googletag;
      if (googletag?.pubads) {
        googletag.pubads().refresh();
      }
    } catch {
      // Error refreshing ads - silently ignore
    }
  });
}

/**
 * Increment navigation counter - call this on route change
 * Only increments if the pathname has actually changed to avoid
 * multiple increments from different components on the same navigation
 * Also destroys existing slots to allow re-defining them
 */
export function incrementNavigationCounter(pathname?: string): void {
  if (pathname && pathname === lastPathname) {
    return; // Same pathname, don't increment
  }
  if (pathname) {
    lastPathname = pathname;
  }
  navigationCounter++;

  // Skip destroying slots during infinite scroll URL updates (cosmetic replaceState).
  // Inline ad slots use unique IDs per instance and manage their own cleanup on unmount.
  if (!infiniteScrollActive) {
    destroyAllSlots();
  }
}

/**
 * Destroy all existing ad slots
 * Call this before re-injecting on navigation
 */
function destroyAllSlots(): void {
  if (!(window as any).googletag?.destroySlots) return;

  try {
    const googletag = (window as any).googletag;
    const slots = googletag.pubads?.()?.getSlots?.() || [];

    if (slots.length > 0) {
      googletag.destroySlots(slots);
    }
  } catch {
    // Error destroying slots - silently ignore
  }
  // Anchor-format slots have no marker-div id, so the registry is the only
  // way we know they exist. Clear it so the next injection redefines them.
  clearAnchorRegistry();
}

/**
 * Extract the ad unit path from a stored anchor ad code.
 * Accepts either a full HTML template containing
 *   googletag.defineOutOfPageSlot('/network/AdUnit', ...)
 * or a bare ad unit path ('/network/AdUnit') already extracted by the server.
 */
export function extractAnchorAdUnit(adCode: string): string | null {
  if (!adCode) return null;
  const trimmed = adCode.trim();
  // Already a bare ad unit path
  if (trimmed.startsWith('/') && !trimmed.includes('<') && !trimmed.includes('googletag')) {
    return trimmed;
  }
  const cleaned = cleanAdCode(adCode);
  const m = cleaned.match(/defineOutOfPageSlot\(\s*['"]([^'"]+)['"]/);
  return m ? m[1] : null;
}

// Track ad units we've already defined as anchor slots in this session so we
// don't call defineOutOfPageSlot twice for the same path (GPT rejects dupes).
const definedAnchorUnits = new Set<string>();

/**
 * Define and display a BOTTOM_ANCHOR slot for the given ad unit path.
 * Uses googletag.enums.OutOfPageFormat.BOTTOM_ANCHOR, which lets GPT request
 * anchor inventory directly — no legacy out-of-page creative trafficking
 * required in GAM.
 */
export function injectAnchorAd(adUnitPath: string): void {
  if (!adUnitPath) return;

  initGPT();
  const googletag = (window as any).googletag;
  googletag.cmd = googletag.cmd || [];

  googletag.cmd.push(() => {
    try {
      // If this anchor unit is already defined from a previous injection,
      // refresh it instead of redefining (GPT silently drops dupes).
      if (definedAnchorUnits.has(adUnitPath)) {
        const existing = googletag.pubads().getSlots().find(
          (s: any) => s.getAdUnitPath() === adUnitPath
        );
        if (existing) {
          googletag.pubads().refresh([existing]);
          return;
        }
      }

      const fmt = googletag.enums?.OutOfPageFormat?.BOTTOM_ANCHOR;
      if (fmt === undefined) return;

      const slot = googletag.defineOutOfPageSlot(adUnitPath, fmt);
      // Slot is null on unsupported pages (e.g., missing viewport meta).
      if (!slot) return;

      slot.addService(googletag.pubads());
      googletag.enableServices();
      googletag.display(slot);
      definedAnchorUnits.add(adUnitPath);
    } catch {
      // Swallow — GPT errors here would just leave the anchor unfilled.
    }
  });
}

/**
 * Drop tracked anchor units. Call when destroying all slots so the next
 * injectAnchorAd() will redefine instead of trying to refresh a dead slot.
 */
export function clearAnchorRegistry(): void {
  definedAnchorUnits.clear();
}

/**
 * Inject ad into a container element
 * Slots are destroyed on navigation, so original IDs can be reused
 */
export function injectAd(
  container: HTMLElement,
  adCode: string,
  position: string
): void {
  // Clear container first to ensure clean injection
  container.innerHTML = '';

  const cleanedCode = cleanAdCode(adCode);

  if (!cleanedCode || cleanedCode.trim() === '') {
    return;
  }

  const { html, scripts } = parseAdCode(cleanedCode);

  // Set HTML content first (DOM elements must exist before scripts run).
  // Anchor (out-of-page) slots must NOT be wrapped: GPT keys the slot to the
  // marker div's containing block when probing for an anchor position, and a
  // flex/centered parent can suppress the anchor bar entirely. Inline slots
  // still get centered so fixed-width creatives align nicely.
  const isAnchor = position.toLowerCase().includes('anchor');
  container.innerHTML = isAnchor
    ? html
    : `<div style="display:flex;justify-content:center;align-items:center;text-align:center;width:100%;">${html}</div>`;

  // Collect div IDs for GPT slots defined in this ad code
  const gptDivIds: string[] = [];
  const divMatches = html.match(/id=['"]([^'"]*div-gpt-ad-[^'"]*)['"]/g);
  if (divMatches) {
    divMatches.forEach(m => {
      const id = m.replace(/id=['"]|['"]/g, '');
      gptDivIds.push(id);
    });
  }

  // Execute scripts, then refresh slots after they're defined
  if (scripts.length > 0) {
    executeAdScripts(scripts);
  }

  // Refresh slots AFTER ad scripts have executed (defineSlot/enableServices/display)
  // Push directly to googletag.cmd — GPT processes commands in order when ready
  if (gptDivIds.length > 0) {
    const googletag = (window as any).googletag;
    googletag.cmd.push(() => {
      const allSlots = googletag.pubads().getSlots();
      const newSlots = allSlots.filter((slot: any) =>
        gptDivIds.includes(slot.getSlotElementId())
      );
      if (newSlots.length > 0) {
        googletag.pubads().refresh(newSlots);
      }

      googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
        const divId = event.slot?.getSlotElementId();
        if (!divId || !gptDivIds.includes(divId)) return;
        const adDiv = document.getElementById(divId);
        if (!adDiv) return;
        if (event.isEmpty) {
          // Hide only the empty ad div, not the entire container
          // This allows fallback ads to render if defined
          adDiv.style.display = 'none';
        }
      });
    });
  }
}
